import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { DEFAULT_PRICING_CONFIG, computePestPricing } from "../lib/pestPricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fetches pest pricing config from the backend once on mount and computes
 * price breakdown from form inputs. All calculators use this hook so the
 * pricing logic lives in exactly one place.
 *
 * @param {object} inputs - see computePestPricing
 * @returns {{ prices: object, pricing: object|null, loading: boolean }}
 */
export function usePestPricing(inputs) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/pest-pricing`);
        if (!cancelled) setPricing(response.data);
      } catch {
        if (!cancelled) setPricing(DEFAULT_PRICING_CONFIG);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const prices = useMemo(
    () => computePestPricing(inputs, pricing),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: recompute only on primitive input changes, not on new object identity
    [
      inputs.propertyType,
      inputs.bedrooms,
      inputs.storeys,
      inputs.treatmentType,
      inputs.serviceFrequency,
      inputs.rodentTreatment,
      inputs.termiteAddOn,
      pricing,
    ]
  );

  return { prices, pricing, loading };
}
